import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BlueprintIcon,
  CaretDownIcon,
  ChatTeardropTextIcon,
  CheckIcon,
  ClockCounterClockwiseIcon,
  CubeIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { useAgentSession } from '@/api-models/agent-sessions';
import { useRepositories } from '@/api-models/repositories';
import {
  revertSessionPlan,
  startCodingSession,
  updateSessionPlan,
  useSessionPlan,
  useSessionPlanVersions,
} from '@/api-models/session-plans';
import { teamPath, useSpace } from '@/contexts/space-context';
import { LocationBar } from '@/shared/shell/LocationBar';
import { Button } from '@/ui/button/Button';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { Spinner } from '@/ui/Spinner';
import { Textarea } from '@/ui/Textarea';

import { usePlanPassage, useStagedPassageHighlights } from '../hooks/usePlanPassage';
import { useSelectionStore } from '../stores/selection-store';

import { SelectionToolbar } from '../components/SelectionToolbar';
import { DEFAULT_PANEL_WIDTH, MIN_PANEL_WIDTH, MIN_THREAD_WIDTH } from '../components/panel-layout';
import { ResizeHandle } from '../components/ResizeHandle';
import { makeRepoLabel, sessionTitle } from '../components/session-controls';
import { SessionHeaderFacts } from '../components/SessionHeaderFacts';
import { SessionStatusPill } from '../components/SessionStatusPill';
import { AssistantMarkdown } from '../components/thread/AssistantMarkdown';
import { ThreadColumn } from '../components/ThreadColumn';

/**
 * The plan view: a conversation on the left, the plan document on the right.
 *
 * Distinct from {@link AgentSessionPage} rather than a mode of it, because the right-hand column
 * is the opposite thing. `SidePanel`'s tabs all resolve through `ensure_session_sandbox_live`, so
 * mounting even one of them here would provision the sandbox this whole session exists to defer.
 *
 * The plan document is authored by the agent (through a reserved `PLAN.md` write) and by the user
 * (through the editor below). Both append versions, so nothing either party wrote is ever lost.
 */
export function PlanSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { teamId } = useSpace();
  const navigate = useNavigate();
  const { session, isLoading } = useAgentSession(sessionId);
  const { repositories } = useRepositories(teamId);
  const repoLabel = makeRepoLabel(repositories);

  // The agent rewrites the plan mid-turn and there is no push channel for the document, so poll
  // while this is still a planning session and stop once it hands off.
  const planning = session?.mode === 'plan';
  const { plan, versions, mutate } = useSessionPlan(sessionId, planning);

  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const splitRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // A session that has already started coding has nothing to plan — send it to its own view
  // rather than showing a document it can no longer change.
  useEffect(() => {
    if (session && session.mode === 'code') {
      navigate(teamPath(teamId, `/sessions/${session.id}`), { replace: true });
    }
  }, [session, teamId, navigate]);

  return (
    <div className="flex h-full flex-col leading-tight">
      <LocationBar>
        <Button
          variant="ghost"
          shape="square"
          size={8}
          onClick={() => navigate(teamPath(teamId, '/sessions'))}
          aria-label="Back to sessions"
        >
          <ArrowLeftIcon size={15} />
        </Button>
        <BlueprintIcon size={16} weight="bold" className="shrink-0 text-brand-text" />
        {session ? (
          <>
            <SessionHeaderFacts
              title={sessionTitle(session)}
              repo={repoLabel(session.repository_id)}
              branch={session.base_branch}
            />
            <SessionStatusPill phase="planning" />
          </>
        ) : (
          <span className="text-sm font-semibold text-fg">Plan</span>
        )}
      </LocationBar>

      {isLoading && !session ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner size={8} />
        </div>
      ) : !session ? (
        <div className="flex flex-1 items-center justify-center text-sm text-fg-tertiary">Session not found.</div>
      ) : (
        <div ref={splitRef} className="flex min-h-0 flex-1">
          <ThreadColumn session={session} hidden={false} onSessionChanged={() => void mutate()} />
          <ResizeHandle
            panelRef={panelRef}
            width={panelWidth}
            onWidth={setPanelWidth}
            min={MIN_PANEL_WIDTH}
            max={() => (splitRef.current?.clientWidth ?? 0) - MIN_THREAD_WIDTH}
            edge="start"
            onReset={() => setPanelWidth(DEFAULT_PANEL_WIDTH)}
            label="Resize plan panel"
          />
          <PlanPanel
            sessionId={session.id}
            planContent={plan?.content_md ?? null}
            planVersion={plan?.version ?? null}
            versions={versions}
            width={panelWidth}
            panelRef={panelRef}
            onChanged={() => void mutate()}
            onStartedCoding={() => navigate(teamPath(teamId, `/sessions/${session.id}`))}
            onFocusComposer={() => document.querySelector<HTMLElement>('[data-composer-input]')?.focus()}
          />
        </div>
      )}
    </div>
  );
}

/** The plan document: read, edit, browse history, comment, and hand off to a coding session. */
function PlanPanel({
  sessionId,
  planContent,
  planVersion,
  versions,
  width,
  panelRef,
  onChanged,
  onStartedCoding,
  onFocusComposer,
}: {
  sessionId: string;
  planContent: string | null;
  planVersion: number | null;
  versions: { version: number; created_by: string; created_at: string }[];
  width: number;
  panelRef: React.RefObject<HTMLDivElement | null>;
  onChanged: () => void;
  onStartedCoding: () => void;
  onFocusComposer: () => void;
}) {
  const [tab, setTab] = useState<'read' | 'edit'>('read');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const readRef = useRef<HTMLDivElement>(null);

  /**
   * An older version the user is looking at, or `null` for the current one.
   *
   * Browsing history is a read, not a write: picking a version used to restore it immediately,
   * which meant every glance at an old draft appended a new one and the history filled up with
   * versions nobody chose to make. Restoring is now its own button.
   */
  const [viewing, setViewing] = useState<number | null>(null);

  // Bodies are only needed once the user actually opens the history, so the fetch is deferred
  // until then — and stays on afterwards so flicking between versions doesn't refetch.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const { versions: fullVersions } = useSessionPlanVersions(sessionId, historyLoaded);

  const viewed = viewing === null ? null : (fullVersions.find((v) => v.version === viewing) ?? null);
  const isHistorical = viewing !== null && viewing !== planVersion;
  // The body arrives from a second request, so it can lag the selection. Say so rather than
  // falling back to the current plan, which would show one version's text under another's label.
  const loadingVersion = viewing !== null && !viewed;
  const shownContent = viewing === null ? planContent : (viewed?.content_md ?? null);

  // Selecting a passage stages it for the next message rather than posting a comment on its own.
  // Disabled while viewing an older version: the passage would be staged against a document the
  // agent is no longer working from.
  const passage = usePlanPassage(readRef, isHistorical ? null : (viewing ?? planVersion));

  // Subscribe to the whole array and narrow it here, rather than selecting `…filter(…)` from the
  // store. A selector that builds a new array is never `Object.is`-equal to the last one, so
  // `useSyncExternalStore` re-renders on every store read and the component loops forever.
  const selections = useSelectionStore((state) => state.selections);
  const stagedSnippets = useMemo(
    () => selections.filter((sel) => sel.kind === 'plan').map((sel) => sel.snippet),
    [selections],
  );
  useStagedPassageHighlights(readRef, stagedSnippets, shownContent);

  // Re-seed the editor from whatever is on screen, but only while not editing — otherwise the
  // agent saving a revision mid-sentence would wipe what the user is typing.
  useEffect(() => {
    if (tab === 'read') setDraft(shownContent ?? '');
  }, [shownContent, tab]);

  // A new version landing (the agent revising, or a restore) is the current plan by definition,
  // so stop showing an older one — otherwise the pane silently goes stale.
  useEffect(() => {
    setViewing(null);
  }, [planVersion]);

  async function save() {
    setSaving(true);
    try {
      await updateSessionPlan(sessionId, draft);
      onChanged();
      setViewing(null);
      setTab('read');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save the plan');
    } finally {
      setSaving(false);
    }
  }

  /** Make the version being viewed current again, by appending a copy of it. */
  async function restore(version: number) {
    setRestoring(true);
    try {
      const plan = await revertSessionPlan(sessionId, version);
      onChanged();
      setViewing(null);
      toast.success(`v${version} is current again (saved as v${plan.version})`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore that version');
    } finally {
      setRestoring(false);
    }
  }

  async function startCoding() {
    setStarting(true);
    try {
      await startCodingSession(sessionId);
      onStartedCoding();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start the coding session');
      setStarting(false);
    }
  }

  return (
    <div ref={panelRef} style={{ width }} className="flex min-h-0 shrink-0 flex-col overflow-hidden bg-surface-1">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <SegmentedControl
          value={tab}
          onChange={setTab}
          ariaLabel="Plan view"
          options={[
            { value: 'read', label: 'Plan' },
            { value: 'edit', label: 'Edit' },
          ]}
        />

        <div className="relative">
          <Button
            variant="ghost"
            size={7}
            onClick={() => {
              setHistoryLoaded(true);
              setHistoryOpen((v) => !v);
            }}
            disabled={versions.length === 0}
            iconLeft={<ClockCounterClockwiseIcon size={14} />}
          >
            {planVersion ? `v${viewing ?? planVersion}` : 'No versions'}
            <CaretDownIcon size={11} className="ml-1" />
          </Button>
          {historyOpen && (
            <div className="absolute left-0 top-full z-20 mt-1 max-h-80 w-72 overflow-y-auto rounded-lg border border-line bg-surface-2 p-1 shadow-lg">
              {versions.map((v) => {
                const isCurrent = v.version === planVersion;
                const isViewing = v.version === (viewing ?? planVersion);
                return (
                  <button
                    key={v.version}
                    type="button"
                    // Selecting only *shows* the version. Making it current is the explicit
                    // "Restore" action on the banner below.
                    onClick={() => {
                      setViewing(isCurrent ? null : v.version);
                      setHistoryOpen(false);
                      setTab('read');
                    }}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-surface-3 hover:text-fg ${
                      isViewing ? 'bg-surface-3 text-fg' : 'text-fg-secondary'
                    }`}
                  >
                    <CheckIcon size={12} className={isViewing ? 'shrink-0' : 'shrink-0 opacity-0'} />
                    <span className="font-medium">v{v.version}</span>
                    {isCurrent && (
                      <span className="rounded-full bg-success-tint px-1.5 text-2xs font-semibold text-success">
                        Current
                      </span>
                    )}
                    <span className="ml-auto shrink-0 text-2xs text-fg-tertiary">
                      {v.created_by === 'agent' ? 'agent' : 'you'} · {new Date(v.created_at).toLocaleDateString()}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="ml-auto">
          {tab === 'edit' ? (
            <Button variant="primary" size={7} onClick={() => void save()} isLoading={saving}>
              {isHistorical ? `Save as v${(planVersion ?? 0) + 1}` : 'Save version'}
            </Button>
          ) : (
            <Button
              variant="primary"
              size={7}
              onClick={() => void startCoding()}
              isLoading={starting}
              // Coding always builds the *current* plan, never the one being previewed — so
              // restore first if that is what you meant.
              disabled={!planContent || isHistorical}
              iconLeft={<CubeIcon size={14} />}
              title={
                isHistorical
                  ? 'Viewing an older version — restore it first, or go back to the current plan'
                  : planContent
                    ? 'Provision a sandbox and build this plan'
                    : 'The agent has not drafted a plan yet'
              }
            >
              Start coding session
            </Button>
          )}
        </div>
      </div>

      {isHistorical && (
        <div className="flex items-center gap-2 border-b border-line bg-warning-tint px-3 py-2 text-2xs text-warning">
          <ClockCounterClockwiseIcon size={13} className="shrink-0" />
          <span className="min-w-0 truncate">
            Viewing v{viewing} — the current plan is v{planVersion}
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <Button variant="ghost" size={7} onClick={() => setViewing(null)}>
              Back to current
            </Button>
            <Button
              variant="secondary"
              size={7}
              onClick={() => void restore(viewing)}
              isLoading={restoring}
              title="Append a copy of this version, making it the current plan"
            >
              Restore
            </Button>
          </div>
        </div>
      )}

      {tab === 'edit' ? (
        <div className="flex min-h-0 flex-1 flex-col p-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={'# Plan\n\nDescribe the change, file by file…'}
            className="min-h-0 flex-1 resize-none font-mono text-xs leading-5"
          />
        </div>
      ) : loadingVersion ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner size={6} />
        </div>
      ) : !shownContent ? (
        <div className="flex flex-1 items-center justify-center px-8 text-center text-sm text-fg-tertiary">
          The agent writes the plan here as it reads the repository.
        </div>
      ) : (
        <div
          ref={readRef}
          onMouseUp={passage.onMouseUp}
          // `relative` so the popover can be positioned against the scrolled content, and
          // `plan-document` is what scopes the selection styling to this pane.
          className="plan-document relative min-h-0 flex-1 overflow-y-auto px-5 py-4"
        >
          <AssistantMarkdown content={shownContent} variant="document" />
          {passage.popup && (
            <SelectionToolbar
              ref={passage.popoverRef}
              style={{ left: passage.popup.x, top: passage.popup.y }}
              onAdd={passage.attach}
              onCancel={passage.dismiss}
              annotateLabel="Comment on this passage"
            />
          )}
        </div>
      )}

      {stagedSnippets.length > 0 && (
        <button
          type="button"
          onClick={onFocusComposer}
          className="flex items-center gap-1.5 border-t border-line bg-brand-tint px-3 py-2 text-left text-2xs font-medium text-brand-text hover:bg-brand/15"
        >
          <ChatTeardropTextIcon size={13} className="shrink-0" />
          {stagedSnippets.length} {stagedSnippets.length === 1 ? 'passage' : 'passages'} staged — write your instruction
          in the composer and send to get one revision covering all of them.
        </button>
      )}
    </div>
  );
}
